import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, ReactiveFormsModule, Validators, AbstractControl, ValidationErrors } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { finalize } from 'rxjs';
import { AuthService } from '../../services/auth.service';


function passwordMatchValidator(group: AbstractControl): ValidationErrors | null {
  const password = group.get('password')?.value;
  const confirmPassword = group.get('confirmPassword')?.value;

  return password && confirmPassword && password !== confirmPassword
    ? { passwordMismatch: true }
    : null;
}

@Component({
  selector: 'app-reset-password',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, RouterLink],
  templateUrl: './reset-password.component.html',
  styleUrl: './reset-password.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ResetPasswordComponent {
  private readonly fb = inject(FormBuilder);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly authService = inject(AuthService);

  readonly validatingToken = signal(true);
  readonly tokenValid = signal(false);
  readonly tokenError = signal<string | null>(null);
  readonly submitError = signal<string | null>(null);
  readonly submitting = signal(false);
  readonly success = signal(false);

  readonly token = this.route.snapshot.queryParamMap.get('token') ?? '';

  readonly form = this.fb.nonNullable.group(
    {
      password: ['', [
        Validators.required,
        Validators.minLength(8),
        // Validators.pattern(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z\d]).+$/), //requires  upper, lower, number, and symbol
        Validators.pattern(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).+$/),     //requires upper, lower, and number
      ]],
      confirmPassword: ['', [Validators.required]],
    },
    { validators: passwordMatchValidator }
  );

  readonly passwordMismatch = computed(
    () => this.form.hasError('passwordMismatch') && this.form.touched
  );

  constructor() {
    if (!this.token) {
      this.validatingToken.set(false);
      this.tokenValid.set(false);
      this.tokenError.set('This reset link is missing a token.');
      return;
    }

    this.authService
      .validateResetPasswordToken(this.token)
      .pipe(finalize(() => this.validatingToken.set(false)))
      .subscribe({
        next: res => {
          this.tokenValid.set(!!res.valid);
          this.tokenError.set(res.valid ? null : (res.message ?? 'This reset link is invalid or expired.'));
        },
        error: () => {
          this.tokenValid.set(false);
          this.tokenError.set('This reset link is invalid or expired.');
        },
      });
  }

  submit(): void {
    if (!this.tokenValid() || this.form.invalid || this.submitting()) {
      this.form.markAllAsTouched();
      return;
    }

    this.submitting.set(true);
    this.submitError.set(null);

    const { password, confirmPassword } = this.form.getRawValue();

    this.authService
      .resetPassword({
        token: this.token,
        password,
        confirmPassword,
      })
      .pipe(finalize(() => this.submitting.set(false)))
      .subscribe({
        next: () => {
          this.success.set(true);

          setTimeout(() => {
            this.router.navigate(['/login'], {
              queryParams: { reset: 'success' },
            });
          }, 1200);
        },
        error: err => {
          this.submitError.set(
            err?.error?.message ?? 'Unable to reset password. This link may have expired.'
          );
        },
      });
  }
}